import { FC, useState } from "react";

import { Box, Stack } from "@mui/material";
import { ProductDonationRecurrenceInterval } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import {
  canTurnOnFrequency,
  DONATION_FREQUENCIES,
  getFrequenciesShownWhenCollapsed,
} from "../helpers/donationFrequencies";
import { StepSuggestedDonationAmountsFormData } from "../types";

import { FrequencyRow } from "./FrequencyRow/FrequencyRow";

import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { ChevronDown } from "@/icons/outlined";
import { DashboardTranslationKey } from "@/public/@types/translations";

interface GivingFrequenciesAndAmountProps {
  form: UseFormReturn<StepSuggestedDonationAmountsFormData>;
  placeholders: DashboardTranslationKey[];
  handleSwitchChange: (name: keyof StepSuggestedDonationAmountsFormData, checked: boolean) => void;
  defaultExpanded?: boolean;
}

export const GivingFrequenciesAndAmount: FC<GivingFrequenciesAndAmountProps> = ({
  form,
  placeholders,
  handleSwitchChange,
  defaultExpanded = false,
}) => {
  const { t } = useTranslate();
  const { apiFormObject } = useLiveFormEditorContext<"donation-form">();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [frequenciesShownWhenCollapsed, setFrequenciesShownWhenCollapsed] = useState<
    ProductDonationRecurrenceInterval[]
  >(() => getFrequenciesShownWhenCollapsed(form.getValues()));

  const formValues = form.watch();
  const visibleFrequencies = DONATION_FREQUENCIES.filter(
    (frequency) => isExpanded || frequenciesShownWhenCollapsed.includes(frequency.interval)
  );

  const toggleExpanded = () => {
    if (isExpanded) {
      setFrequenciesShownWhenCollapsed(getFrequenciesShownWhenCollapsed(form.getValues()));
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <Stack gap={1}>
      <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate" sx={{ marginBottom: 1 }}>
        {t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.givingFrequenciesAndAmountsTitle")}
      </DashboardTypography>

      {visibleFrequencies.map((frequency) => (
        <FrequencyRow
          key={frequency.interval}
          form={form}
          name={frequency.switchName}
          amountsName={frequency.amountsName}
          labelKey={frequency.labelKey}
          data-test={frequency.switchName}
          placeholders={placeholders}
          locale={apiFormObject.locale}
          isEnabled={formValues[frequency.switchName] && formValues.suggestedAmountsCount > 0}
          isToggleDisabled={!canTurnOnFrequency(formValues, frequency.switchName)}
          onSwitchChange={(checked) => handleSwitchChange(frequency.switchName, checked)}
          amplitudeEventAdded={frequency.amplitudeEventAdded}
          amplitudeEventRemoved={frequency.amplitudeEventRemoved}
        />
      ))}

      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <DashboardButton
          variant="text"
          size="small"
          vibe="neutral"
          endIcon={<ChevronDown />}
          slotProps={{
            endIcon: {
              sx: (theme) => ({
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: theme.transitions.create("transform", { duration: theme.transitions.duration.short }),
              }),
            },
          }}
          onClick={toggleExpanded}
          data-test="frequencies-more-options"
        >
          {isExpanded
            ? t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.lessOptions")
            : t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.moreOptions")}
        </DashboardButton>
      </Box>
    </Stack>
  );
};
